import { ParseKeys, TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import {
  OtherInitiativeCertificate,
  ScopeCertificate,
  TransactionCertificate,
} from "~/pages/Brand/Certificates/components/templates.ts"

type ScopeCertificateKeys = keyof ScopeCertificate
type TransactionCertificateKeys = keyof TransactionCertificate
type OtherInitiativeCertificateKeys = keyof OtherInitiativeCertificate

type FilteredCertificateKeys =
  | ScopeCertificateKeys
  | TransactionCertificateKeys
  | OtherInitiativeCertificateKeys

type TranslationValue = ParseKeys<"certificates">

type TranslationIndexerCertificate = {
  [key in FilteredCertificateKeys]: TranslationValue
}

type TranslationIndexerStandardDescription = {
  success: TranslationValue
  failure: TranslationValue
}

type TranslationIndexerStandard = {
  relevantStandard: TranslationValue
  relevantStandardDesc: TranslationIndexerStandardDescription
  validityPeriod: TranslationValue
  validityPeriodDesc: TranslationIndexerStandardDescription
  certifiedEntity: TranslationValue
  certifiedEntityDesc: TranslationIndexerStandardDescription
  productCategories: TranslationValue
  productCategoriesDesc: TranslationIndexerStandardDescription
  certificationBody: TranslationValue
  certificationBodyDesc: TranslationIndexerStandardDescription
}

export const placeHolderTranslations: TranslationIndexerCertificate = {
  buyerOfCertifiedProducts: "modal.placeholders.buyerOfCertifiedProducts",
  certificationBody: "modal.placeholders.certificationBody",
  certifiedOrganizationLicenseNumber:
    "modal.placeholders.certifiedOrganizationLicenseNumber",
  certifiedOrganizationName: "modal.placeholders.certifiedOrganizationName",
  certifiedWeight: "modal.placeholders.certifiedWeight",
  expiryDate: "modal.placeholders.expiryDate",
  issueDate: "modal.placeholders.issueDate",
  linkedScopeCertificateNumber:
    "modal.placeholders.linkedScopeCertificateNumber",
  name: "modal.placeholders.name",
  processingSteps: "modal.placeholders.processingSteps",
  productCategories: "modal.placeholders.productCategories",
  reference: "modal.placeholders.reference",
  score: "modal.placeholders.score",
  typeId: "modal.placeholders.type",
  certifiedOrganizationAddress:
    "modal.placeholders.certifiedOrganizationAddress",
  productDetails: "modal.placeholders.productDetails",
}

export const labelHolderTranslations: TranslationIndexerCertificate = {
  buyerOfCertifiedProducts: "modal.labels.buyerOfCertifiedProducts",
  certificationBody: "modal.labels.certificationBody",
  certifiedOrganizationLicenseNumber:
    "modal.labels.certifiedOrganizationLicenseNumber",
  certifiedOrganizationName: "modal.labels.certifiedOrganizationName",
  certifiedWeight: "modal.labels.certifiedWeight",
  expiryDate: "modal.labels.expiryDate",
  issueDate: "modal.labels.issueDate",
  linkedScopeCertificateNumber: "modal.labels.linkedScopeCertificateNumber",
  name: "modal.labels.name",
  processingSteps: "modal.labels.processingSteps",
  productCategories: "modal.labels.productCategories",
  reference: "modal.labels.reference",
  score: "modal.labels.score",
  typeId: "modal.labels.type",
  certifiedOrganizationAddress: "modal.labels.certifiedOrganizationAddress",
  productDetails: "modal.labels.productDetails",
}

export const standardRulesTranslations: TranslationIndexerStandard = {
  relevantStandard: "standard.drawer.rules.relevantStandard.title",
  relevantStandardDesc: {
    success: "standard.drawer.rules.relevantStandard.description.success",
    failure: "standard.drawer.rules.relevantStandard.description.failure",
  },
  validityPeriod: "standard.drawer.rules.validityPeriod.title",
  validityPeriodDesc: {
    success: "standard.drawer.rules.validityPeriod.description.success",
    failure: "standard.drawer.rules.validityPeriod.description.failure",
  },
  certifiedEntity: "standard.drawer.rules.certifiedEntity.title",
  certifiedEntityDesc: {
    success: "standard.drawer.rules.certifiedEntity.description.success",
    failure: "standard.drawer.rules.certifiedEntity.description.failure",
  },
  productCategories: "standard.drawer.rules.productCategories.title",
  productCategoriesDesc: {
    success: "standard.drawer.rules.productCategories.description.success",
    failure: "standard.drawer.rules.productCategories.description.failure",
  },
  certificationBody: "standard.drawer.rules.certificationBody.title",
  certificationBodyDesc: {
    success: "standard.drawer.rules.certificationBody.description.success",
    failure: "standard.drawer.rules.certificationBody.description.failure",
  },
}

export type CertificateScore =
  | "notApplicable"
  | "other"
  | "lwgPlatinum"
  | "lwgGold"
  | "lwgSilver"
  | "lwgBronze"
  | "lwgOther"
  | "lwgAudited"
  | "lwgCertified"
  | "bsciA"
  | "bsciB"
  | "bsciC"
  | "bsciD"
  | "bsciE"
  | "bsciOther"
  | "icsA"
  | "icsB"
  | "icsC"
  | "icsD"
  | "icsE"
  | "icsOther"
  | "sa8000Compliant"
  | "sa8000PartlyMinor"
  | "sa8000PartlyMajor"
  | "sa8000NonCompliant"
  | "sa8000Other"
  | "bepiLeader"
  | "bepiAdvanced"
  | "bepiProgressing"
  | "bepiBeginner"
  | "bepiInsufficient"
  | "bepiOther"
  | "wrapPlatinum"
  | "wrapGold"
  | "wrapSilver"
  | "wrapBronze"
  | "wrapOther"
  | "wrapCertified"
  | "gotsCertified"
  | "gotsInConversion"
  | "gotsSuspended"
  | "gotsWithdrawn"
  | "gotsOther"
  | "oekotexStandard100"
  | "oekotexStepLevel1"
  | "oekotexStepLevel2"
  | "oekotexStepLevel3"
  | "oekotexMadeInGreen"
  | "oekotexLeatherStandard"
  | "oekotexEcoPassport"
  | "oekotexOther"
  | "smeta2Pillar"
  | "smeta4Pillar"
  | "smetaNoNonCompliance"
  | "smetaMinorNonCompliance"
  | "smetaMajorNonCompliance"
  | "smetaCriticalNonCompliance"
  | "smetaOther"
  | "higgFemBeginner"
  | "higgFemDeveloping"
  | "higgFemEstablished"
  | "higgFemLeading"
  | "higgFemOther"
  | "rcsCertified"
  | "rcsInConversion"
  | "rcsSuspended"
  | "rcsWithdrawn"
  | "rcsOther"
  | "grsCertified"
  | "grsInConversion"
  | "grsSuspended"
  | "grsWithdrawn"
  | "grsOther"
  | "iso14001Certified"
  | "iso14001NotCertified"
  | "iso14001Suspended"
  | "iso14001Withdrawn"
  | "iso14001Other"
  | "iso45001Certified"
  | "iso45001NotCertified"
  | "iso45001Suspended"
  | "iso45001Withdrawn"
  | "iso45001Other"

export const getScoreTranslation = (
  score: CertificateScore,
  t: TFunction<"certificates">
) => {
  return getScoreTranslations(t)[score]
}

export const getScoreTranslations = (
  t: TFunction<"certificates">
): {
  [key in CertificateScore]: string
} => {
  const scoreTranslations: {
    [key in CertificateScore]: string
  } = {
    notApplicable: t("modal.labels.scoreOptions.notApplicable"),
    other: t("modal.labels.scoreOptions.other"),
    lwgPlatinum: t("modal.labels.scoreOptions.lwg.platinum"),
    lwgGold: t("modal.labels.scoreOptions.lwg.gold"),
    lwgSilver: t("modal.labels.scoreOptions.lwg.silver"),
    lwgBronze: t("modal.labels.scoreOptions.lwg.bronze"),
    lwgOther: t("modal.labels.scoreOptions.lwg.other"),
    lwgAudited: t("modal.labels.scoreOptions.lwg.audited"),
    lwgCertified: t("modal.labels.scoreOptions.lwg.certified"),
    bsciA: t("modal.labels.scoreOptions.bsci.a"),
    bsciB: t("modal.labels.scoreOptions.bsci.b"),
    bsciC: t("modal.labels.scoreOptions.bsci.c"),
    bsciD: t("modal.labels.scoreOptions.bsci.d"),
    bsciE: t("modal.labels.scoreOptions.bsci.e"),
    bsciOther: t("modal.labels.scoreOptions.bsci.other"),

    icsA: t("modal.labels.scoreOptions.ics.a"),
    icsB: t("modal.labels.scoreOptions.ics.b"),
    icsC: t("modal.labels.scoreOptions.ics.c"),
    icsD: t("modal.labels.scoreOptions.ics.d"),
    icsE: t("modal.labels.scoreOptions.ics.e"),
    icsOther: t("modal.labels.scoreOptions.ics.other"),

    sa8000Compliant: t("modal.labels.scoreOptions.sa8000.compliant"),
    sa8000PartlyMinor: t("modal.labels.scoreOptions.sa8000.partlyMinor"),
    sa8000PartlyMajor: t("modal.labels.scoreOptions.sa8000.partlyMajor"),
    sa8000NonCompliant: t("modal.labels.scoreOptions.sa8000.nonCompliant"),
    sa8000Other: t("modal.labels.scoreOptions.sa8000.other"),

    bepiLeader: t("modal.labels.scoreOptions.bepi.leader"),
    bepiAdvanced: t("modal.labels.scoreOptions.bepi.advanced"),
    bepiProgressing: t("modal.labels.scoreOptions.bepi.progressing"),
    bepiBeginner: t("modal.labels.scoreOptions.bepi.beginner"),
    bepiInsufficient: t("modal.labels.scoreOptions.bepi.insufficient"),
    bepiOther: t("modal.labels.scoreOptions.bepi.other"),

    wrapPlatinum: t("modal.labels.scoreOptions.wrap.platinum"),
    wrapGold: t("modal.labels.scoreOptions.wrap.gold"),
    wrapSilver: t("modal.labels.scoreOptions.wrap.silver"),
    wrapBronze: t("modal.labels.scoreOptions.wrap.bronze"),
    wrapCertified: t("modal.labels.scoreOptions.wrap.certified"),
    wrapOther: t("modal.labels.scoreOptions.wrap.other"),

    gotsCertified: t("modal.labels.scoreOptions.gots.certified"),
    gotsInConversion: t("modal.labels.scoreOptions.gots.inConversion"),
    gotsSuspended: t("modal.labels.scoreOptions.gots.suspended"),
    gotsWithdrawn: t("modal.labels.scoreOptions.gots.withdrawn"),
    gotsOther: t("modal.labels.scoreOptions.gots.other"),

    oekotexStandard100: t("modal.labels.scoreOptions.oekotex.standard100"),
    oekotexStepLevel1: t("modal.labels.scoreOptions.oekotex.stepLevel1"),
    oekotexStepLevel2: t("modal.labels.scoreOptions.oekotex.stepLevel2"),
    oekotexStepLevel3: t("modal.labels.scoreOptions.oekotex.stepLevel3"),
    oekotexMadeInGreen: t("modal.labels.scoreOptions.oekotex.madeInGreen"),
    oekotexLeatherStandard: t(
      "modal.labels.scoreOptions.oekotex.leatherStandard"
    ),
    oekotexEcoPassport: t("modal.labels.scoreOptions.oekotex.ecoPassport"),
    oekotexOther: t("modal.labels.scoreOptions.oekotex.other"),

    smeta2Pillar: t("modal.labels.scoreOptions.smeta.twoPillar"),
    smeta4Pillar: t("modal.labels.scoreOptions.smeta.fourPillar"),
    smetaNoNonCompliance: t("modal.labels.scoreOptions.smeta.noNonCompliance"),
    smetaMinorNonCompliance: t(
      "modal.labels.scoreOptions.smeta.minorNonCompliance"
    ),
    smetaMajorNonCompliance: t(
      "modal.labels.scoreOptions.smeta.majorNonCompliance"
    ),
    smetaCriticalNonCompliance: t(
      "modal.labels.scoreOptions.smeta.criticalNonCompliance"
    ),
    smetaOther: t("modal.labels.scoreOptions.smeta.other"),

    higgFemBeginner: t("modal.labels.scoreOptions.higgFem.beginner"),
    higgFemDeveloping: t("modal.labels.scoreOptions.higgFem.developing"),
    higgFemEstablished: t("modal.labels.scoreOptions.higgFem.established"),
    higgFemLeading: t("modal.labels.scoreOptions.higgFem.leading"),
    higgFemOther: t("modal.labels.scoreOptions.higgFem.other"),

    rcsCertified: t("modal.labels.scoreOptions.rcs.certified"),
    rcsInConversion: t("modal.labels.scoreOptions.rcs.inConversion"),
    rcsSuspended: t("modal.labels.scoreOptions.rcs.suspended"),
    rcsWithdrawn: t("modal.labels.scoreOptions.rcs.withdrawn"),
    rcsOther: t("modal.labels.scoreOptions.rcs.other"),

    grsCertified: t("modal.labels.scoreOptions.grs.certified"),
    grsInConversion: t("modal.labels.scoreOptions.grs.inConversion"),
    grsSuspended: t("modal.labels.scoreOptions.grs.suspended"),
    grsWithdrawn: t("modal.labels.scoreOptions.grs.withdrawn"),
    grsOther: t("modal.labels.scoreOptions.grs.other"),

    iso14001Certified: t("modal.labels.scoreOptions.iso14001.certified"),
    iso14001NotCertified: t("modal.labels.scoreOptions.iso14001.notCertified"),
    iso14001Suspended: t("modal.labels.scoreOptions.iso14001.suspended"),
    iso14001Withdrawn: t("modal.labels.scoreOptions.iso14001.withdrawn"),
    iso14001Other: t("modal.labels.scoreOptions.iso14001.other"),

    iso45001Certified: t("modal.labels.scoreOptions.iso45001.certified"),
    iso45001NotCertified: t("modal.labels.scoreOptions.iso45001.notCertified"),
    iso45001Suspended: t("modal.labels.scoreOptions.iso45001.suspended"),
    iso45001Withdrawn: t("modal.labels.scoreOptions.iso45001.withdrawn"),
    iso45001Other: t("modal.labels.scoreOptions.iso45001.other"),
  }

  return scoreTranslations
}

type TooltipTranslationIndexerCertificate = {
  [key in keyof Pick<TranslationIndexerCertificate, "score">]: string
}

export const getTooltipTranslation = (
  tooltipKey: keyof TranslationIndexerCertificate,
  t: TFunction<"certificates">
) => {
  const tooltipTranslation = getTooltipTranslations(t)
  if (!(tooltipKey in tooltipTranslation)) {
    return undefined
  }

  return tooltipTranslation[
    tooltipKey as keyof TooltipTranslationIndexerCertificate
  ]
}

export const getTooltipTranslations = (
  t: TFunction<"certificates">
): TooltipTranslationIndexerCertificate => {
  const tooltipTranslations: TooltipTranslationIndexerCertificate = {
    score: t("modal.labels.scoreTooltip"),
  }

  return tooltipTranslations
}

export function useReacti18Translation() {
  const { t } = useTranslation(["certificates", "common"])

  // t("certificates:modal.placeholders.buyerOfCertifiedProducts")
  // t("certificates:modal.placeholders.certificationBody")
  // t("certificates:modal.placeholders.certifiedOrganizationLicenseNumber")
  // t("certificates:modal.placeholders.certifiedOrganizationName")
  // t("certificates:modal.placeholders.certifiedWeight")
  // t("certificates:modal.placeholders.expiryDate")
  // t("certificates:modal.placeholders.issueDate")
  // t("certificates:modal.placeholders.linkedScopeCertificateNumber")
  // t("certificates:modal.placeholders.name")
  // t("certificates:modal.placeholders.processingSteps")
  // t("certificates:modal.placeholders.productCategories")
  // t("certificates:modal.placeholders.reference")
  // t("certificates:modal.placeholders.rejectionMessage")
  // t("certificates:modal.placeholders.score")
  // t("certificates:modal.placeholders.status")
  // t("certificates:modal.placeholders.type")
  // t("certificates:modal.placeholders.certifiedOrganizationAddress")
  // t("certificates:modal.placeholders.productDetails")

  // t("certificates:modal.labels.buyerOfCertifiedProducts")
  // t("certificates:modal.labels.certificationBody")
  // t("certificates:modal.labels.certifiedOrganizationLicenseNumber")
  // t("certificates:modal.labels.certifiedOrganizationName")
  // t("certificates:modal.labels.certifiedWeight")
  // t("certificates:modal.labels.expiryDate")
  // t("certificates:modal.labels.issueDate")
  // t("certificates:modal.labels.linkedScopeCertificateNumber")
  // t("certificates:modal.labels.name")
  // t("certificates:modal.labels.processingSteps")
  // t("certificates:modal.labels.productCategories")
  // t("certificates:modal.labels.reference")
  // t("certificates:modal.labels.rejectionMessage")
  // t("certificates:modal.labels.score")
  // t("certificates:modal.labels.status")
  // t("certificates:modal.labels.type")
  // t("certificates:modal.labels.certifiedOrganizationAddress")
  // t("certificates:modal.labels.productDetails")

  // t("certificates:standard.drawer.rules.relevantStandard.title")
  // t("certificates:standard.drawer.rules.relevantStandard.description.success")
  // t("certificates:standard.drawer.rules.relevantStandard.description.failure")
  // t("certificates:standard.drawer.rules.validityPeriod.title")
  // t("certificates:standard.drawer.rules.validityPeriod.description.success")
  // t("certificates:standard.drawer.rules.validityPeriod.description.failure")
  // t("certificates:standard.drawer.rules.certifiedEntity.title")
  // t("certificates:standard.drawer.rules.certifiedEntity.description.success")
  // t("certificates:standard.drawer.rules.certifiedEntity.description.failure")
  // t("certificates:standard.drawer.rules.productCategories.title")
  // t("certificates:standard.drawer.rules.productCategories.description.success")
  // t("certificates:standard.drawer.rules.productCategories.description.failure")
  // t("certificates:standard.drawer.rules.certificationBody.title")
  // t("certificates:standard.drawer.rules.certificationBody.description.success")
  // t("certificates:standard.drawer.rules.certificationBody.description.failure")

  // t("common:table.dateFilter.operator.isEmpty")
  // t("common:table.dateFilter.operator.isNotEmpty")
  // t("common:table.dateFilter.operator.between")
  // t("common:table.dateFilter.operator.equals")
  // t("common:table.dateFilter.operator.before")
  // t("common:table.dateFilter.operator.after")

  void t
}
